/**
 * AuditTemplateManager - Professional template builder & library
 * 
 * Features:
 *   - Template library with search, filter, status badges
 *   - Full visual builder with sections, items (tasks), drag reorder
 *   - Response types: four_level, binary, numeric, text
 *   - Evidence rules, ISO references, scoring weights
 *   - Version history with publish/draft workflow
 */

import React, { useState, useEffect } from 'react';
import {
  Plus, Save, ArrowLeft, GripVertical, Trash2, Edit2, FileText,
  ListChecks, ChevronDown, ChevronRight, Search,
  CheckCircle2, Hash, Camera, BookOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Badge } from '@shared/components/ui/badge';
import { Switch } from '@shared/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@shared/components/ui/dialog';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';

const AUDIT_TYPES = [
  { value: 'safety_tour',              label: 'Safety Tour',              icon: '🦺' },
  { value: 'planned_audit',            label: 'Planned Audit',            icon: '📋' },
  { value: 'dynamic_risk_assessment',  label: 'Dynamic Risk Assessment',  icon: '⚡' },
  { value: 'environmental',            label: 'Environmental',            icon: '🌿' },
  { value: 'quality',                  label: 'Quality',                  icon: '✅' },
  { value: 'inspection',              label: 'Inspection',               icon: '🔍' },
  { value: 'health_safety',           label: 'Health & Safety',          icon: '🏥' },
  { value: 'management_system',       label: 'Management System',        icon: '📊' },
  { value: 'supplier',                label: 'Supplier',                 icon: '🤝' },
  { value: 'compliance',              label: 'Compliance',               icon: '📜' },
  { value: 'uploaded_audit',           label: 'Uploaded Audit',           icon: '📤' },
];

const RESPONSE_TYPES = [
  { value: 'four_level', label: 'Compliant / NC / Observation / N/A', description: 'Standard 4-level response' },
  { value: 'binary',     label: 'Yes / No',           description: 'Simple pass/fail' },
  { value: 'numeric',    label: 'Numeric Score',      description: 'Score 0-10' },
  { value: 'text',       label: 'Text Only',          description: 'Free-text response' },
];

const EVIDENCE_RULES = [
  { value: 'never',                      label: 'No evidence' },
  { value: 'optional',                   label: 'Optional' },
  { value: 'always_required',            label: 'Always required' },
  { value: 'required_if_nc',             label: 'Required if NC' },
  { value: 'required_if_at_risk_or_nc',  label: 'Required if At Risk or NC' },
];

// ─── Template Library View ──────────────────────────────────────────
function TemplateLibrary({ templates, onSelect, onCreate, isLoading }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const filtered = templates.filter(t => {
    const matchesSearch = !search || 
      t.template_name.toLowerCase().includes(search.toLowerCase()) ||
      t.template_code?.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || t.audit_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const statusBadge = (t) => {
    if (!t.is_active) return <Badge variant="outline" className="text-gray-500 border-gray-300">Archived</Badge>;
    if (t.currentVersion?.status === 'published') return <Badge className="bg-green-500/10 text-green-700 border-green-500/30">Published</Badge>;
    return <Badge variant="outline" className="text-amber-600 border-amber-400">Draft</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Template Library</h2>
          <p className="text-sm text-muted-foreground">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {AUDIT_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Card key={i} className="h-40 animate-pulse bg-muted/20" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">No templates found</p>
          <Button variant="outline" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-2" /> Create your first template
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <Card
              key={t.id}
              className="cursor-pointer hover:border-amber-500/50 hover:shadow-md transition-all group"
              onClick={() => onSelect(t)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm truncate group-hover:text-amber-600 transition-colors">
                      {t.template_name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.template_code}</p>
                  </div>
                  {statusBadge(t)}
                </div>
                {t.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{t.description}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ListChecks className="h-3 w-3" />
                    {t.sectionCount || 0} section{(t.sectionCount || 0) !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    {t.itemCount || 0} item{(t.itemCount || 0) !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center gap-1 ml-auto">
                    {(() => { const at = AUDIT_TYPES.find(at => at.value === t.audit_type); return at ? `${at.icon} ${at.label}` : t.audit_type; })()}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Checklist Item Editor Row ──────────────────────────────────────
function ItemRow({ item, index, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const responseLabel = RESPONSE_TYPES.find(r => r.value === item.response_type)?.label || item.response_type;
  const evidenceLabel = EVIDENCE_RULES.find(e => e.value === item.evidence_required)?.label || item.evidence_required;

  return (
    <div className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border/50 group hover:border-amber-500/30 transition-colors">
      <div className="flex flex-col gap-0.5 text-muted-foreground/50 shrink-0">
        <button
          onClick={() => onMoveUp(index)}
          disabled={isFirst}
          className="hover:text-foreground disabled:opacity-20 transition-colors p-0.5"
        >
          <ChevronRight className="h-3 w-3 -rotate-90" />
        </button>
        <GripVertical className="h-4 w-4 cursor-grab" />
        <button
          onClick={() => onMoveDown(index)}
          disabled={isLast}
          className="hover:text-foreground disabled:opacity-20 transition-colors p-0.5"
        >
          <ChevronRight className="h-3 w-3 rotate-90" />
        </button>
      </div>

      <div className="font-mono text-xs text-muted-foreground w-8 text-center shrink-0">
        {item.item_code || `${index + 1}`}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.item_text}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px] h-5">{responseLabel}</Badge>
          <Badge variant="outline" className="text-[10px] h-5">
            <Camera className="h-2.5 w-2.5 mr-1" />{evidenceLabel}
          </Badge>
          {item.requirement_reference && (
            <Badge variant="outline" className="text-[10px] h-5 text-blue-600 border-blue-300">
              <BookOpen className="h-2.5 w-2.5 mr-1" />{item.requirement_reference}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(index)}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => onDelete(index)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Section Card ───────────────────────────────────────────────────
function SectionCard({ section, sectionIndex, onEditSection, onDeleteSection, onAddItem, onEditItem, onDeleteItem, onMoveItem }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card className="border-l-4 border-l-amber-500/50">
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{section.section_code}</span>
            <h3 className="font-semibold text-sm">{section.section_title}</h3>
            <Badge variant="outline" className="text-[10px]">
              {section.items.length} item{section.items.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          {section.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{section.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onAddItem(sectionIndex)}>
            <Plus className="h-3 w-3 mr-1" /> Item
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditSection(sectionIndex)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDeleteSection(sectionIndex)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {section.items.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground border border-dashed rounded-lg">
              No items yet.{' '}
              <button className="text-amber-600 hover:underline" onClick={() => onAddItem(sectionIndex)}>
                Add the first item
              </button>
            </div>
          ) : (
            section.items.map((item, itemIndex) => (
              <ItemRow
                key={itemIndex}
                item={item}
                index={itemIndex}
                onEdit={(idx) => onEditItem(sectionIndex, idx)}
                onDelete={(idx) => onDeleteItem(sectionIndex, idx)}
                onMoveUp={(idx) => onMoveItem(sectionIndex, idx, 'up')}
                onMoveDown={(idx) => onMoveItem(sectionIndex, idx, 'down')}
                isFirst={itemIndex === 0}
                isLast={itemIndex === section.items.length - 1}
              />
            ))
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Item Editor Modal ──────────────────────────────────────────────
function ItemEditorModal({ open, onClose, item, onSave }) {
  const [form, setForm] = useState(item || {});

  useEffect(() => {
    if (item) setForm({ ...item });
  }, [item]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item?.item_text ? 'Edit Item' : 'Add Item'}</DialogTitle>
          <DialogDescription>Define the checklist item, response type, and evidence requirements.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item Text */}
          <div className="space-y-1.5">
            <Label>Task / Question *</Label>
            <Textarea
              placeholder="e.g. Are all workers wearing hard hats in designated areas?"
              value={form.item_text || ''}
              onChange={e => update('item_text', e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Item Code */}
            <div className="space-y-1.5">
              <Label>Item Code</Label>
              <Input
                placeholder="e.g. 1.1"
                value={form.item_code || ''}
                onChange={e => update('item_code', e.target.value)}
              />
            </div>
            {/* Score Weight */}
            <div className="space-y-1.5">
              <Label>Score Weight</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={form.score_weight ?? 1}
                onChange={e => update('score_weight', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Response Type */}
          <div className="space-y-1.5">
            <Label>Response Type *</Label>
            <Select value={form.response_type || 'four_level'} onValueChange={v => update('response_type', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESPONSE_TYPES.map(r => (
                  <SelectItem key={r.value} value={r.value} textValue={r.label}>
                    <div>
                      <div className="font-medium">{r.label}</div>
                      <div className="text-xs text-muted-foreground">{r.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Evidence */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Evidence Required</Label>
              <Select value={form.evidence_required || 'optional'} onValueChange={v => update('evidence_required', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVIDENCE_RULES.map(e => (
                    <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Min Photos</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={form.min_photo_count ?? 0}
                onChange={e => update('min_photo_count', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Guidance */}
          <div className="space-y-1.5">
            <Label>Guidance Notes</Label>
            <Textarea
              placeholder="Instructions or guidance for the auditor..."
              value={form.guidance_notes || ''}
              onChange={e => update('guidance_notes', e.target.value)}
              rows={2}
            />
          </div>

          {/* ISO Reference */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Requirement Reference</Label>
              <Input
                placeholder="e.g. ISO 45001:2018 §8.1.2"
                value={form.requirement_reference || ''}
                onChange={e => update('requirement_reference', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Regulatory Source</Label>
              <Input
                placeholder="e.g. ISO 45001, AS/NZS 4801"
                value={form.regulatory_source || ''}
                onChange={e => update('regulatory_source', e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.item_text?.trim()) {
              toast({ title: 'Required', description: 'Item text is required', variant: 'destructive' });
              return;
            }
            onSave(form);
          }}>
            <Save className="h-4 w-4 mr-2" /> Save Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Section Editor Modal ───────────────────────────────────────────
function SectionEditorModal({ open, onClose, section, onSave }) {
  const [form, setForm] = useState(section || {});

  useEffect(() => {
    if (section) setForm({ ...section });
  }, [section]);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{section?.section_title ? 'Edit Section' : 'Add Section'}</DialogTitle>
          <DialogDescription>Define a section to group related audit items.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Section Title *</Label>
            <Input
              placeholder="e.g. PPE Requirements"
              value={form.section_title || ''}
              onChange={e => update('section_title', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Section Code</Label>
              <Input
                placeholder="e.g. 1.0"
                value={form.section_code || ''}
                onChange={e => update('section_code', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Weight</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={form.weight ?? 1}
                onChange={e => update('weight', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              placeholder="Brief description of this section..."
              value={form.description || ''}
              onChange={e => update('description', e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.section_title?.trim()) {
              toast({ title: 'Required', description: 'Section title is required', variant: 'destructive' });
              return;
            }
            onSave(form);
          }}>
            <Save className="h-4 w-4 mr-2" /> Save Section
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Template Builder View ──────────────────────────────────────────
function TemplateBuilder({ template, user, onBack, onSaved }) {
  const [templateData, setTemplateData] = useState({
    template_name: template?.template_name || '',
    template_code: template?.template_code || '',
    description: template?.description || '',
    audit_type: template?.audit_type || 'safety_tour',
    depot_id: template?.depot_id || null,
  });

  const [versionData, setVersionData] = useState({
    version_label: '',
    change_summary: '',
    enable_scoring: true,
    pass_threshold_percentage: 85,
  });

  const [sections, setSections] = useState([]);
  const [depots, setDepots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadDepots();
    if (template?.id) loadExistingTemplate(template.id);
  }, [template?.id]);

  const loadDepots = async () => {
    const { data } = await supabase.from('depots').select('id, name').eq('is_active', true).order('name');
    setDepots(data || []);
  };

  const loadExistingTemplate = async (templateId) => {
    try {
      setLoading(true);
      // Get current version with sections and items
      const { data: version } = await supabase
        .from('audit_template_versions')
        .select('*')
        .eq('template_id', templateId)
        .eq('is_current_version', true)
        .maybeSingle();

      if (!version) return;

      setVersionData({
        version_label: version.version_label || '',
        change_summary: '',
        enable_scoring: version.enable_scoring ?? true,
        pass_threshold_percentage: version.pass_threshold_percentage ?? 85,
      });

      // Get sections
      const { data: sectionData } = await supabase
        .from('audit_template_sections')
        .select('*')
        .eq('version_id', version.id)
        .order('section_order');

      // Get items for all sections
      const { data: itemData } = await supabase
        .from('audit_template_items')
        .select('*')
        .eq('version_id', version.id)
        .order('item_order');

      const loadedSections = (sectionData || []).map(s => ({
        ...s,
        items: (itemData || []).filter(item => item.section_id === s.id),
      }));

      setSections(loadedSections);
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Section CRUD ─────────────────────────────────────────────
  const handleAddSection = () => {
    setEditingSectionIndex(null);
    setEditingSection({
      section_title: '', section_code: `${sections.length + 1}.0`,
      description: '', section_order: sections.length, weight: 1, items: [],
    });
    setSectionModalOpen(true);
  };

  const handleEditSection = (index) => {
    setEditingSectionIndex(index);
    setEditingSection({ ...sections[index] });
    setSectionModalOpen(true);
  };

  const handleSaveSection = (form) => {
    if (editingSectionIndex !== null) {
      const updated = [...sections];
      updated[editingSectionIndex] = { ...updated[editingSectionIndex], ...form };
      setSections(updated);
    } else {
      setSections([...sections, { ...form, items: form.items || [] }]);
    }
    setSectionModalOpen(false);
  };

  const handleDeleteSection = (index) => {
    if (confirm('Delete this section and all its items?')) {
      setSections(sections.filter((_, i) => i !== index));
    }
  };

  // ─── Item CRUD ────────────────────────────────────────────────
  const handleAddItem = (sectionIndex) => {
    setActiveSectionIndex(sectionIndex);
    setEditingItemIndex(null);
    const section = sections[sectionIndex];
    setEditingItem({
      item_text: '', item_code: `${section.section_code || sectionIndex + 1}.${section.items.length + 1}`,
      item_order: section.items.length, guidance_notes: '', requirement_reference: '',
      regulatory_source: '', response_type: 'four_level', score_weight: 1,
      max_score: 10, evidence_required: 'optional', min_photo_count: 0,
    });
    setItemModalOpen(true);
  };

  const handleEditItem = (sectionIndex, itemIndex) => {
    setActiveSectionIndex(sectionIndex);
    setEditingItemIndex(itemIndex);
    setEditingItem({ ...sections[sectionIndex].items[itemIndex] });
    setItemModalOpen(true);
  };

  const handleSaveItem = (form) => {
    const updated = [...sections];
    if (editingItemIndex !== null) {
      updated[activeSectionIndex].items[editingItemIndex] = { ...form, item_order: editingItemIndex };
    } else {
      updated[activeSectionIndex].items.push({ ...form, item_order: updated[activeSectionIndex].items.length });
    }
    setSections(updated);
    setItemModalOpen(false);
  };

  const handleDeleteItem = (sectionIndex, itemIndex) => {
    const updated = [...sections];
    updated[sectionIndex].items = updated[sectionIndex].items.filter((_, i) => i !== itemIndex);
    setSections(updated);
  };

  const handleMoveItem = (sectionIndex, itemIndex, direction) => {
    const updated = [...sections];
    const items = [...updated[sectionIndex].items];
    const targetIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [items[itemIndex], items[targetIndex]] = [items[targetIndex], items[itemIndex]];
    items.forEach((item, i) => item.item_order = i);
    updated[sectionIndex].items = items;
    setSections(updated);
  };

  // ─── Save to Database ─────────────────────────────────────────
  const handleSave = async (publish = false) => {
    if (!templateData.template_name.trim()) {
      toast({ title: 'Validation', description: 'Template name is required', variant: 'destructive' });
      return;
    }
    if (sections.length === 0) {
      toast({ title: 'Validation', description: 'Add at least one section', variant: 'destructive' });
      return;
    }
    const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
    if (totalItems === 0) {
      toast({ title: 'Validation', description: 'Add at least one item', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      let templateId = template?.id;

      // Create or update template
      if (!templateId) {
        const baseCode = templateData.template_code || templateData.template_name.toUpperCase().replace(/\s+/g, '-').slice(0, 25);
        const code = `${baseCode}-${Date.now().toString(36).slice(-4)}`;
        const { data: newT, error } = await supabase
          .from('audit_templates')
          .insert({ ...templateData, template_code: code, created_by: user.id })
          .select()
          .single();
        if (error) throw error;
        templateId = newT.id;
      } else {
        await supabase.from('audit_templates').update({
          template_name: templateData.template_name,
          description: templateData.description,
          audit_type: templateData.audit_type,
          depot_id: templateData.depot_id,
        }).eq('id', templateId);
      }

      // Deactivate old versions' is_current_version flag
      if (templateId) {
        await supabase.from('audit_template_versions')
          .update({ is_current_version: false })
          .eq('template_id', templateId)
          .eq('is_current_version', true);
      }

      // Get next version number
      const { data: existingVersions } = await supabase
        .from('audit_template_versions')
        .select('version_number')
        .eq('template_id', templateId)
        .order('version_number', { ascending: false })
        .limit(1);
      const nextVersionNumber = (existingVersions?.[0]?.version_number || 0) + 1;

      // Create version
      const { data: version, error: vError } = await supabase
        .from('audit_template_versions')
        .insert({
          template_id: templateId,
          version_number: nextVersionNumber,
          version_label: versionData.version_label || `v${nextVersionNumber}.0`,
          change_summary: versionData.change_summary || (nextVersionNumber === 1 ? 'Initial version' : 'Updated version'),
          enable_scoring: versionData.enable_scoring,
          pass_threshold_percentage: versionData.pass_threshold_percentage,
          status: publish ? 'published' : 'draft',
          published_at: publish ? new Date().toISOString() : null,
          published_by: publish ? user.id : null,
          is_current_version: true,
          created_by: user.id,
        })
        .select()
        .single();

      if (vError) throw vError;

      // Create sections + items
      for (const section of sections) {
        const { data: savedSection, error: sError } = await supabase
          .from('audit_template_sections')
          .insert({
            version_id: version.id,
            section_title: section.section_title,
            section_code: section.section_code,
            description: section.description,
            section_order: section.section_order,
            weight: section.weight,
          })
          .select()
          .single();

        if (sError) throw sError;

        if (section.items.length > 0) {
          const itemRows = section.items.map((item, idx) => ({
            version_id: version.id,
            section_id: savedSection.id,
            item_text: item.item_text,
            item_code: item.item_code,
            item_order: idx,
            guidance_notes: item.guidance_notes,
            requirement_reference: item.requirement_reference,
            regulatory_source: item.regulatory_source,
            response_type: item.response_type,
            score_weight: item.score_weight,
            max_score: item.max_score,
            evidence_required: item.evidence_required,
            min_photo_count: item.min_photo_count,
          }));

          const { error: iError } = await supabase
            .from('audit_template_items')
            .insert(itemRows);

          if (iError) throw iError;
        }
      }

      toast({
        title: publish ? 'Template Published' : 'Template Saved',
        description: `${templateData.template_name} saved with ${sections.length} sections and ${totalItems} items`,
      });

      onSaved?.();
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: 'Error', description: error.message || 'Failed to save template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Builder Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-lg font-semibold">{template?.id ? 'Edit Template' : 'New Template'}</h2>
            <p className="text-xs text-muted-foreground">
              {sections.length} section{sections.length !== 1 ? 's' : ''} · {totalItems} item{totalItems !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" /> Save Draft
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <CheckCircle2 className="h-4 w-4 mr-2" /> Publish
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <Card key={i} className="h-32 animate-pulse bg-muted/20" />)}
        </div>
      ) : (
        <>
          {/* Template Metadata */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Template Name *</Label>
                  <Input
                    placeholder="e.g. Monthly Safety Inspection"
                    value={templateData.template_name}
                    onChange={e => setTemplateData(d => ({ ...d, template_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Template Code</Label>
                  <Input
                    placeholder="Auto-generated if empty"
                    value={templateData.template_code}
                    onChange={e => setTemplateData(d => ({ ...d, template_code: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of this audit template..."
                  value={templateData.description}
                  onChange={e => setTemplateData(d => ({ ...d, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Audit Type</Label>
                  <Select value={templateData.audit_type} onValueChange={v => setTemplateData(d => ({ ...d, audit_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AUDIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Depot</Label>
                  <Select
                    value={templateData.depot_id || 'all'}
                    onValueChange={v => setTemplateData(d => ({ ...d, depot_id: v === 'all' ? null : v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Depots</SelectItem>
                      {depots.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Pass Threshold</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={versionData.pass_threshold_percentage}
                      onChange={e => setVersionData(v => ({ ...v, pass_threshold_percentage: parseInt(e.target.value) || 0 }))}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={versionData.enable_scoring}
                  onCheckedChange={v => setVersionData(d => ({ ...d, enable_scoring: v }))}
                />
                <Label className="text-sm">Enable scoring</Label>
              </div>
            </CardContent>
          </Card>

          {/* Sections & Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Sections & Items</h3>
              <Button variant="outline" size="sm" onClick={handleAddSection}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Section
              </Button>
            </div>

            {sections.length === 0 ? (
              <Card className="p-12 text-center border-dashed">
                <ListChecks className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="font-medium text-muted-foreground">No sections yet</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Start by adding a section like "PPE Requirements" or "Site Safety"
                </p>
                <Button variant="outline" onClick={handleAddSection}>
                  <Plus className="h-4 w-4 mr-2" /> Add First Section
                </Button>
              </Card>
            ) : (
              sections.map((section, idx) => (
                <SectionCard
                  key={idx}
                  section={section}
                  sectionIndex={idx}
                  onEditSection={handleEditSection}
                  onDeleteSection={handleDeleteSection}
                  onAddItem={handleAddItem}
                  onEditItem={handleEditItem}
                  onDeleteItem={handleDeleteItem}
                  onMoveItem={handleMoveItem}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <SectionEditorModal
        open={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        section={editingSection}
        onSave={handleSaveSection}
      />
      <ItemEditorModal
        open={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        item={editingItem}
        onSave={handleSaveItem}
      />
    </div>
  );
}

// ─── Main Template Manager ──────────────────────────────────────────
export default function AuditTemplateManager({ user, onNavigate: _onNavigate }) {
  const [view, setView] = useState('library'); // library | builder
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('audit_templates')
        .select(`
          *,
          audit_template_versions (
            id, version_label, status, is_current_version, created_at
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Enrich with counts
      const enriched = await Promise.all((data || []).map(async (t) => {
        const currentVersion = t.audit_template_versions?.find(v => v.is_current_version);

        let sectionCount = 0;
        let itemCount = 0;

        if (currentVersion) {
          const [{ count: sc }, { count: ic }] = await Promise.all([
            supabase.from('audit_template_sections').select('*', { count: 'exact', head: true }).eq('version_id', currentVersion.id),
            supabase.from('audit_template_items').select('*', { count: 'exact', head: true }).eq('version_id', currentVersion.id),
          ]);
          sectionCount = sc || 0;
          itemCount = ic || 0;
        }

        return { ...t, currentVersion, sectionCount, itemCount };
      }));

      setTemplates(enriched);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (view === 'builder') {
    return (
      <TemplateBuilder
        template={selectedTemplate}
        user={user}
        onBack={() => { setView('library'); setSelectedTemplate(null); }}
        onSaved={() => { setView('library'); setSelectedTemplate(null); loadTemplates(); }}
      />
    );
  }

  return (
    <TemplateLibrary
      templates={templates}
      isLoading={isLoading}
      onSelect={(t) => { setSelectedTemplate(t); setView('builder'); }}
      onCreate={() => { setSelectedTemplate(null); setView('builder'); }}
    />
  );
}
